import PropTypes from 'prop-types';
import React from 'react';

import cls from 'classnames/bind';

import Button from '@ott/button';
import { lang, l10n } from '@ott/l10n';
import { withLayoutContextForwardRef } from '@ott/layout-context';
import LoadingDots from '@ott/loading-dots';

import HeaderAuthIcon from './blocks/HeaderAuthIcon';
import HeaderProfileIcon from './blocks/HeaderProfileIcon';
import PremiumStatusBadgeIcon from './blocks/PremiumStatusBadgeIcon';
import ProStatusBadgeIcon from './blocks/ProStatusBadgeIcon';
import constants from './constants/common';
import ITEMS_CONFIG from './constants/items';
import headerAuthHelpers from './helpers';

import styles from './HeaderAuth.scss';

const cx = cls.bind(styles);

const { MENU_ITEMS, POSITION, STATUSES } = constants;

export { MENU_ITEMS, POSITION, headerAuthHelpers };

class HeaderAuth extends React.PureComponent {
  static propTypes = {
    isAuthenticated: PropTypes.bool.isRequired,
    userStatus: PropTypes.oneOf(STATUSES),
    onShowAuthButtonClick: PropTypes.func.isRequired,
    onExit: PropTypes.func.isRequired,
    isAuthLoading: PropTypes.bool.isRequired,
    userHasVirtualCard: PropTypes.bool.isRequired,
    // Items displayed alongside the auth element
    menuItemsConfig: PropTypes.shape({
      authorized: PropTypes.oneOfType([
        PropTypes.shape({
          desktop: PropTypes.arrayOf(PropTypes.string),
          mobile: PropTypes.arrayOf(PropTypes.string),
        }),
        PropTypes.arrayOf(PropTypes.string),
      ]),
      notAuthorized: PropTypes.oneOfType([
        PropTypes.shape({
          desktop: PropTypes.arrayOf(PropTypes.string),
          mobile: PropTypes.arrayOf(PropTypes.string),
        }),
        PropTypes.arrayOf(PropTypes.string),
      ]),
    }),

    // Items displayed in the menu dropdown
    dropdownMenuItemsConfig: PropTypes.shape({
      authorized: PropTypes.oneOfType([
        PropTypes.shape({
          desktop: PropTypes.arrayOf(PropTypes.string),
          mobile: PropTypes.arrayOf(PropTypes.string),
        }),
        PropTypes.arrayOf(PropTypes.string),
      ]),
      notAuthorized: PropTypes.oneOfType([
        PropTypes.shape({
          desktop: PropTypes.arrayOf(PropTypes.string),
          mobile: PropTypes.arrayOf(PropTypes.string),
        }),
        PropTypes.arrayOf(PropTypes.string),
      ]),
    }),
    // set false for partners and other locales than 'ru'
    shouldDisplayStatusBadge: PropTypes.bool,
    // set true for partners header
    useHeaderPartnerStyles: PropTypes.bool,
    // set false in case several header-auth components
    partner: PropTypes.object,
    hasProfile: PropTypes.bool,
    position: PropTypes.oneOf(Object.values(POSITION)),

    specificLocators: PropTypes.shape({
      menuItems: PropTypes.shape(
        Object.keys(MENU_ITEMS).reduce((types, item) => {
          types[item] = PropTypes.string;

          return types;
        }, {})
      ),
      authLabelAuthorized: PropTypes.string,
      authLabelUnauthorized: PropTypes.string,
    }),

    childrenClassNames: PropTypes.shape({
      profileMenuClassName: PropTypes.string,
    }),
  };

  static defaultProps = {
    hasProfile: true,
    shouldDisplayStatusBadge: true,
    useHeaderPartnerStyles: false,
    position: POSITION.inplace,
    specificLocators: {},
    childrenClassNames: {},
  };

  constructor(props) {
    super(props);
    this.mobileMenuRef = React.createRef();
    this.profileRef = React.createRef();
    this.menuAuthRef = React.createRef();
  }

  state = { isMenuOpen: false };

  handleClickOutside = (event) => {
    const { isMenuOpen } = this.state;

    const { current: mobileMenuEl } = this.mobileMenuRef;
    const { current: menuAuthEl } = this.menuAuthRef;
    const { current: profileEl } = this.profileRef;

    const isExistedMobileMenuAuth = mobileMenuEl && !mobileMenuEl.contains(event.target);
    const isExistedMobileMenuUnAuth = menuAuthEl && !menuAuthEl.contains(event.target);

    if (isExistedMobileMenuAuth || isExistedMobileMenuUnAuth) {
      if (profileEl && profileEl.contains(event.target) && isMenuOpen) {
        return; // клик по профилю -> пропускаем тут -> сработает toogle
      }
      this.setState({ isMenuOpen: false });
    }
  };

  // Делаем проверку через viewportWidth, чтобы правила стилей и поведение в скриптак было синхронным
  checkIsDesktop() {
    const { isDesktop, viewportWidth } = this.props;
    const squeezeAuthMaxWidth = Number(cx('squeezeAuthMaxWidth'));

    if (!Number.isNaN(squeezeAuthMaxWidth)) {
      return viewportWidth >= squeezeAuthMaxWidth;
    }

    return isDesktop;
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside, true);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside, true);
    window.removeEventListener('click', this.handleClick);
  }

  setContentRef = (el) => {
    this.contentRef = el;
  };

  handleClick = (e) => {
    const { isAuthLoading } = this.props;

    if (isAuthLoading) {
      return;
    }

    const { isMenuOpen } = this.state;
    this.setState({ isMenuOpen: !isMenuOpen });
  };

  handleSignInShow = (e) => {
    const { onShowAuthButtonClick, isAuthLoading } = this.props;

    if (isAuthLoading) {
      return;
    }

    onShowAuthButtonClick();
  };

  handleExit = (e) => {
    const { onExit } = this.props;

    onExit();
  };

  getMenuItems = (menuItemsConfig) => {
    const { partner, isAuthenticated, userStatus } = this.props;
    // Get the first level category by auth state
    const menuItemsCategory = isAuthenticated ? 'authorized' : 'notAuthorized';
    const configByCategory = menuItemsConfig[menuItemsCategory];

    // Get the second level category by adaptivity state
    const hasAdaptiveConfig =
      configByCategory.hasOwnProperty('mobile') && configByCategory.hasOwnProperty('desktop');
    const adaptiveCategory = this.checkIsDesktop() ? 'desktop' : 'mobile';

    const dropdownMenuItemsList = hasAdaptiveConfig
      ? configByCategory[adaptiveCategory]
      : configByCategory;

    return dropdownMenuItemsList
      .filter(headerAuthHelpers.filterItem({ isAuthenticated, userStatus, partner, lang }))
      .map((menuItemName) => this.getMenuItemConfig(menuItemName));
  };

  getMenuItemConfig = (menuItemName) => {
    return {
      name: menuItemName,
      config: ITEMS_CONFIG[menuItemName],
    };
  };

  getMenuItemLocator = ({ config } = {}) => {
    const {
      specificLocators: { menuItems = {} },
    } = this.props;
    if (config) {
      return menuItems[config.key] || config.locator || config.key;
    }

    return '';
  };

  renderLoadingDots = () => {
    const { useHeaderPartnerStyles } = this.props;

    return (
      <div className={cx('loadingDotsContainer')}>
        <LoadingDots isWhite={!useHeaderPartnerStyles} />
      </div>
    );
  };

  renderLogin = (item, itemClassName) => {
    const { isAuthLoading, partner, hasProfile } = this.props;

    if (!hasProfile || (partner && partner.disableProfile)) {
      return null;
    }

    return (
      <Button
        size="medium"
        purpose="neutral"
        className={cls(styles.login, styles.menuItem, styles[itemClassName], {
          [styles.headerPartnerLogin]: !!partner,
        })}
        key={item.config.key}
        data-href={`//m.onetwotrip.com/${lang}/auth/`}
        onClick={this.handleSignInShow}
        specificLocators={this.getMenuItemLocator(item)}
      >
        {isAuthLoading ? this.renderLoadingDots() : l10n(item.config.l10nKey)}
      </Button>
    );
  };

  renderLogout = (item, itemClassName) => {
    const { hasProfile, partner } = this.props;

    if (!hasProfile || (partner && partner.disableProfile)) {
      return null;
    }

    return (
      <span
        key={item.config.key}
        className={cx('menu-item', itemClassName)}
        onClick={this.handleExit}
        data-locator={this.getMenuItemLocator(item)}
      >
        {l10n(item.config.l10nKey)}
      </span>
    );
  };

  renderStatusBadge = () => {
    const { userStatus } = this.props;

    switch (userStatus) {
      case STATUSES.pro:
        return (
          <ProStatusBadgeIcon className={cx('statusBadge')} specificLocators="pro-status-badge" />
        );
      case STATUSES.premium:
        return (
          <PremiumStatusBadgeIcon
            className={cx('statusBadge')}
            specificLocators="premium-status-badge"
          />
        );
      default:
        return null;
    }
  };

  renderMobileLogin = (item, itemClassName) => {
    const { hasProfile, partner } = this.props;

    if (!hasProfile || (partner && partner.disableProfile)) {
      return null;
    }

    return (
      <span
        key={item.config.key}
        className={cx('menu-item', item.config.key, itemClassName)}
        data-locator={this.getMenuItemLocator(item)}
        onClick={this.handleSignInShow}
      >
        {l10n(item.config.l10nKey)}
      </span>
    );
  };

  renderContentNode = () => {
    const {
      userStatus,
      isAuthLoading,
      isAuthenticated,
      partner,
      shouldDisplayStatusBadge,
      specificLocators: { authLabelAuthorized, authLabelUnauthorized },
      childrenClassNames,
    } = this.props;
    const { isMenuOpen } = this.state;

    if (isAuthenticated) {
      const displayStatusBadge =
        !partner &&
        shouldDisplayStatusBadge &&
        lang === 'ru' &&
        (userStatus === STATUSES.pro || userStatus === STATUSES.premium);

      const profileLabelNode = displayStatusBadge ? (
        <div key="profileLabel" className={cx('profileLabel', `profileLabel--${userStatus}`)}>
          {this.renderStatusBadge()}
          <HeaderProfileIcon className={cx('icon')} />
        </div>
      ) : (
        <div key="profileLabel" className={cx('profileLabel')}>
          <HeaderProfileIcon className={cx('icon')} />
        </div>
      );

      return [
        <div key="menuItems" className={cx('menuItems')}>
          <div
            data-locator={cls('authLabel', authLabelAuthorized, {
              'no-status-badge': !displayStatusBadge,
            })}
            className={cx('profileWrapper')}
            onClick={this.handleClick}
            key="profileWrapper"
            ref={this.profileRef}
          >
            {profileLabelNode}
          </div>
          <aside
            key="profileMenu"
            data-locator="profile-menu"
            className={cx('profileMenu', {
              'profileMenu--isMenuOpen': isMenuOpen,
            })}
            ref={this.mobileMenuRef}
          >
            {this.renderDropdownMenuItems()}
          </aside>
        </div>,
      ];
    }

    return [
      <div
        key="menuItems"
        className={cx('menuItems', {
          'menuItems--isMenuAuthOpen': isMenuOpen,
        })}
        ref={this.menuAuthRef}
      >
        {this.renderMenuItems()}
        <div
          key="authLabel"
          className={cx('authLabel')}
          data-locator={cls('authLabel', authLabelUnauthorized)}
          onClick={this.handleClick}
        >
          {isAuthLoading ? this.renderLoadingDots() : <HeaderAuthIcon className={cx('icon')} />}
        </div>
        <aside
          key="menuAuth"
          className={cx('menuAuth', childrenClassNames.profileMenuClassName, {
            'menuAuth--isMenuOpen': isMenuOpen,
          })}
        >
          {this.renderDropdownMenuItems()}
        </aside>
      </div>,
    ];
  };

  renderDropdownMenuItems = () => {
    const { dropdownMenuItemsConfig = headerAuthHelpers.getProductsDropdownMenuItemsConfig() } =
      this.props;
    const menuItems = this.getMenuItems(dropdownMenuItemsConfig);

    return menuItems.map(this.menuItemRenderer);
  };

  renderMenuItems = () => {
    const { menuItemsConfig = headerAuthHelpers.getProductsMenuItemsConfig() } = this.props;
    const menuItems = this.getMenuItems(menuItemsConfig);

    return menuItems.map(this.menuItemRenderer);
  };

  getHrefLink = (item) => {
    if (item.config.key === MENU_ITEMS.supportLink) {
      return item.config.url;
    }

    let href = `/${lang}${item.config.url}`;

    if (typeof window !== 'undefined') {
      if (window.location.hostname.includes('.kz')) {
        const hostname = window.location.hostname.replace('.kz', '.com');
        href = `${window.location.protocol}//${hostname}/${lang}${item.config.url}`;
      }
    }

    return href;
  };

  getVirtualCardUrl = (config) => {
    const { userHasVirtualCard } = this.props;

    const { url, authorizedUrl } = config;

    return userHasVirtualCard ? authorizedUrl : url;
  };

  renderLinkMenuItem = (item, menuItemClass) => {
    return (
      <a
        key={item.config.key}
        className={cx('menu-item', item.config.key, menuItemClass)}
        target="_blank"
        href={this.getHrefLink(item)}
        data-locator={this.getMenuItemLocator(item)}
        rel="noreferrer"
      >
        {l10n(item.config.l10nKey)}
      </a>
    );
  };

  menuItemRenderer = (item) => {
    const adaptiveCategory = this.checkIsDesktop() ? 'desktop' : 'mobile';
    const menuItemClass = `menu-item--${adaptiveCategory}`;

    switch (item.name) {
      case MENU_ITEMS.login:
      case MENU_ITEMS.profileLogin: {
        return this.renderLogin(item, menuItemClass);
      }
      case MENU_ITEMS.exit: {
        return this.renderLogout(item, menuItemClass);
      }
      case MENU_ITEMS.mobileLogin: {
        return this.renderMobileLogin(item, menuItemClass);
      }
      case MENU_ITEMS.virtualcard: {
        const customItem = {
          ...item,
          config: {
            ...item.config,
            url: this.getVirtualCardUrl(item.config),
          },
        };

        return this.renderLinkMenuItem(customItem, menuItemClass);
      }
      case MENU_ITEMS.order:
      case MENU_ITEMS.profile:
      case MENU_ITEMS.achievements:
      case MENU_ITEMS.travelexpert:
      case MENU_ITEMS.orderOtt:
      case MENU_ITEMS.supportLink: {
        return this.renderLinkMenuItem(item, menuItemClass);
      }
    }

    return null;
  };

  render() {
    const { className, partner, position, useHeaderPartnerStyles } = this.props;

    return (
      <div
        key="headerAuth"
        className={cls(className, styles.HeaderAuth, styles[`HeaderAuth--${position}`], {
          [styles['HeaderAuth--partner']]: Boolean(partner) && useHeaderPartnerStyles,
        })}
        ref={this.setContentRef}
      >
        {this.renderContentNode()}
      </div>
    );
  }
}

export default withLayoutContextForwardRef(HeaderAuth);
